import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Trash2, Calendar as CalendarIcon, Info, X } from 'lucide-react';
import APIRequest from '../../api/APIRequest';
import { useSelector } from 'react-redux';
import Loader from '../ui/Loader';
import { useToast } from '../../contexts/ToastContext';
import CustomDatePicker from '../ui/CustomDatePicker';

const CalendarManagement = ({ selectedMonth, classId, onEventChange }) => {
  const { user } = useSelector((state) => state.auth);
  const { success, error } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    date: '',
    type: 'holiday',
    description: '',
    classId: ''
  });

  const fetchEvents = async () => {
    if (!selectedMonth) return;
    setLoading(true);
    try {
      const { startDate, endDate } = getMonthStartEnd(selectedMonth);
      const apiRequest = new APIRequest();
      const response = await apiRequest.request(
        `/admin/calendar/get-events?startDate=${startDate}&endDate=${endDate}&classId=${classId || ''}`,
        'GET',
        null,
        user?.token
      );
      if (response.success) {
        setEvents(response.data);
      }
    } catch (err) {
      console.error('Error fetching calendar events:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [selectedMonth, classId]);

  const getMonthStartEnd = (month) => {
    const [year, monthNum] = month.split('-').map(Number);
    const startDate = new Date(year, monthNum - 1, 1);
    const endDate = new Date(year, monthNum, 0);
    endDate.setHours(23, 59, 59, 999);
    return {
      startDate: startDate.getTime(),
      endDate: endDate.getTime()
    };
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.date || !newEvent.description) {
      error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const apiRequest = new APIRequest();
      const payload = {
        ...newEvent,
        date: new Date(newEvent.date).getTime(),
        classId: newEvent.classId || null
      };

      const response = await apiRequest.request('/admin/calendar/add-event', 'POST', payload, user?.token);
      if (response.success) {
        success('Event added successfully');
        setShowAddModal(false);
        setNewEvent({ date: '', type: 'holiday', description: '', classId: '' });
        fetchEvents();
        if (onEventChange) onEventChange();
      } else {
        error(response.message || 'Failed to add event');
      }
    } catch (err) {
      console.error('Error adding event:', err);
      error('Failed to add event');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    setLoading(true);
    try {
      const apiRequest = new APIRequest();
      const response = await apiRequest.request(`/admin/calendar/delete-event/${id}`, 'DELETE', null, user?.token);
      if (response.success) {
        success('Event deleted successfully');
        fetchEvents();
        if (onEventChange) onEventChange();
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      error('Failed to delete event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-[2rem] shadow-xl shadow-blue-900/5 border border-slate-100 overflow-hidden mt-8 animate-fadeIn">
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-black text-slate-800 tracking-tight">Special Days / Holidays</h3>
            <p className="text-slate-400 text-sm font-medium mt-1">Manage school-wide and class-specific holidays</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg shadow-blue-600/20 active:scale-95"
          >
            <Plus size={18} />
            <span className="text-xs uppercase tracking-widest">Add Event</span>
          </button>
        </div>

        {loading && events.length === 0 ? (
          <div className="flex justify-center p-8">
            <Loader />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
            <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No special days marked for this month</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((schEvent) => (
              <div
                key={schEvent._id}
                className="p-5 bg-slate-50 rounded-2xl border border-slate-100 flex justify-between items-start group hover:bg-white hover:shadow-md transition-all duration-300"
              >
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${
                      schEvent.type === 'holiday' ? 'bg-rose-100 text-rose-600' :
                      schEvent.type === 'half-day' ? 'bg-amber-100 text-amber-600' :
                      'bg-indigo-100 text-indigo-600'
                    }`}>
                      {schEvent.type}
                    </span>
                    <span className="text-xs font-bold text-slate-400">
                      {new Date(schEvent.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                  <h4 className="text-sm font-bold text-slate-800">{schEvent.description}</h4>
                  {schEvent.classId && (
                    <span className="text-[10px] text-blue-500 font-bold uppercase mt-1 block">Class Specific</span>
                  )}
                </div>
                <button
                  onClick={() => handleDeleteEvent(schEvent._id)}
                  className="p-2 text-slate-400 hover:text-rose-600 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Event Modal - Use Portal to escape stacking context */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md shadow-2xl animate-modalIn relative">
            <div className="p-8 max-h-[90vh] overflow-y-auto no-scrollbar">
              <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10 pb-2">
                <h3 className="text-2xl font-black text-slate-800 tracking-tight">Add Special Day</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <X className="text-slate-400" />
                </button>
              </div>

              <form onSubmit={handleAddEvent} className="space-y-6">
                <div>
                  <CustomDatePicker
                    name="date"
                    label="Event Date"
                    required
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                    title="Select Event Date"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1 mb-2 block">Event Type</label>
                  <select
                    value={newEvent.type}
                    onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-bold text-slate-700"
                  >
                    <option value="holiday">Full Holiday</option>
                    <option value="half-day">Half Day</option>
                    <option value="school-leave">School Leave</option>
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1 mb-2 block">Description</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Independence Day"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-bold text-slate-700"
                  />
                </div>

                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-2xl">
                  <Info className="text-blue-500 shrink-0" size={20} />
                  <p className="text-xs text-blue-700 font-medium italic">
                    Events added here will reflect in the attendance calendar for all relevant students.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl transition-all shadow-lg shadow-blue-600/20 active:scale-[0.98] disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Save Event'}
                </button>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modalIn { animation: modalIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </div>
  );
};

export default CalendarManagement;
